import { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../../../api/axios';
import dayjs from 'dayjs';
import { Box, Card, Typography, InputBase, Button, Stack, Grid, FormControl, InputLabel, Autocomplete, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { Save, Edit, Close, Delete } from '@mui/icons-material';
import UpdatePassword from './UpdatePassword';
import Loading from '../../../components/common/loading/Loading';
import { notifySuccess, notifyError } from "../../../components/common/toast/ToastFunc";
import usStatesList from '../../../assests/data/usStatesList';

const CustomInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    height: "33px",
    borderRadius: 4,
    position: 'relative',
    backgroundColor: 'white',
    border: '1px solid grey',
    fontSize: 16,
    width: '100%',
    padding: '10px 12px',
    '&.Mui-disabled': {
      color: 'black',
      fontWeight: 'bold',
      backgroundColor: '#f2f2f2'
    },
  },
}));

const Profile = (props) => {

  const { externalUser } = props;

  const [originalData, setOriginalData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [crudLoading, setCrudLoading] = useState(false);
  const [reFetch, setReFetch] = useState(false);

  const theme = useTheme();

  const buttonTheme = useMemo(() => {
    return {
      color: 'white', backgroundColor: theme.mainColor, fontSize: 15, fontWeight: "bold", borderRadius: 2,
      '&:hover': {
        backgroundColor: '#b30000', color: 'white', fontSize: 15, fontWeight: "bold", borderRadius: 2
      },
    }
  }, [theme.mainColor]);

  const handleEditProfile = () => {
    setEditData(prev => ({ ...prev }))
    setEditMode(true);
  };

  const handleCancelProfile = () => {
    setEditMode(false);
    setEditData(originalData); // Reset changes
  };

  const handleSaveProfile = async () => {
    try {
      setCrudLoading(true);
      let formData = editData;
      delete formData["user_id"]
      const response = await axiosInstance.post('/customer/user-info/update-profile', editData);
      notifySuccess(response?.data?.message || "Success");
      setEditMode(false);
      setReFetch(!reFetch);
    } catch (error) {
      notifyError(error.message || "Something went wrong");
    } finally {
      setCrudLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    try {
      setCrudLoading(true);
      const response = await axiosInstance.post('/auth/authorize-profile-transaction', { id: originalData["user_id"], auth_type: "Delete" });
      notifySuccess(response?.data?.message || "Success");
    } catch (error) {
      notifyError(error.message || "Something went wrong");
    } finally {
      setCrudLoading(false);
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        setOriginalData(null);
        setEditData(null);
        const response = await axiosInstance.get('/customer/user-info/get-data');
        const data = {
          ...response.data.data,
          dob: dayjs(response.data.data["dob"]).format('MM/DD/YYYY'),
        }
        setOriginalData(data);
        setEditData(data);
      } catch (error) {
        notifyError(error.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    }
    getData();
  }, [reFetch]);

  const renderTextField = (label, name, disabled) => (
    <FormControl variant="standard" fullWidth sx={{ mb: 0 }}>
      <InputLabel shrink htmlFor={`${name}-input`} sx={{ fontWeight: "bold" }}>
        {label}
      </InputLabel>
      <CustomInput
        id={`${name}-input`}
        name={name}
        value={editData[name]}
        onChange={(e) => setEditData((prevData) => ({ ...prevData, [name]: e.target.value }))}
        disabled={disabled}
      />
    </FormControl>
  );

  return (
    <>
      {loading && <Box mt={5}><Loading /></Box>}
      {editData &&
        <Stack direction="column" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 5 }}>
          <Card sx={{ p: 3, borderRadius: "7px", maxWidth: 800, display: "flex", flexDirection: "column", justifyItems: "center", alignItems: "center" }}>
            <Typography align="center" sx={{ fontSize: "28px", mt: -2, mb: 2, color: theme.mainColor }}>{editMode ? 'Edit Profile Information' : 'Profile Information'}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>{renderTextField('Email', 'email_id', true)}</Grid>
              <Grid item xs={12} sm={5}>{renderTextField('User ID', 'user_id', true)}</Grid>
              <Grid item xs={12} sm={3}>{renderTextField('Role', 'role', true)}</Grid>

              <Grid item xs={12} sm={3}>{renderTextField('First Name', 'first_name', !editMode)}</Grid>
              <Grid item xs={12} sm={3}>{renderTextField('Last Name', 'last_name', !editMode)}</Grid>
              <Grid item xs={12} sm={3}>{renderTextField('Mobile No', 'mobile_no', true)}</Grid>

              <Grid item xs={12} sm={3}>{renderTextField('DOB', 'dob', true)}</Grid>
              <Grid item xs={12} sm={3}>{renderTextField('Address', 'address', !editMode)}</Grid>
              <Grid item xs={12} sm={3}>{renderTextField('City', 'city', !editMode)}</Grid>

              {/* State, Zip Code, Password */}
              <Grid item xs={12} sm={3}>
                {editMode ? (
                  <Autocomplete
                    options={usStatesList}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        variant="outlined"
                        name="state"
                      />
                    )}
                    value={usStatesList.find(state => state.name === editData.state) || null}
                    onChange={(event, newValue) => {
                      setEditData((prevData) => ({ ...prevData, state: newValue ? newValue.name : '' }));
                    }}
                    ListboxProps={{ style: { maxHeight: '200px', overflow: 'auto', borderRadius: '8px' } }}
                    sx={{ width: '100%', mt: 3 }}
                  />
                ) : (
                  renderTextField('State', 'state', true)
                )}
              </Grid>
              <Grid item xs={12} sm={3}>{renderTextField('Zip Code', 'zip_code', !editMode)}</Grid>
            </Grid>
            <Box sx={{ display: 'flex', mt: 3 }}>
              {editMode ? (
                <>
                  <Button onClick={handleCancelProfile} startIcon={<Close />} sx={{ mr: 1, color: theme.mainColor }}>Cancel</Button>
                  <LoadingButton
                    onClick={handleSaveProfile}
                    loading={crudLoading}
                    disabled={crudLoading}
                    loadingPosition="start"
                    startIcon={<Save />}
                    variant="contained"
                    sx={{ ...buttonTheme }}
                  >
                    Save
                  </LoadingButton>
                </>
              ) : (
                <>
                  <Button variant="contained" onClick={() => handleEditProfile()} startIcon={<Edit />} disabled={crudLoading} sx={{ ...buttonTheme }}>Edit Profile</Button>
                  {!externalUser &&
                    <LoadingButton
                      onClick={() => handleDeleteProfile()}
                      loading={crudLoading}
                      disabled={crudLoading}
                      loadingPosition="start"
                      startIcon={<Delete />}
                      variant="contained"
                      sx={{ ...buttonTheme, ml: 2 }}
                    >
                      Delete Account
                    </LoadingButton>
                  }
                </>
              )}
            </Box>
          </Card>
          <Card sx={{ p: 3, borderRadius: "7px", maxWidth: 800, display: "flex", flexDirection: "column" }}>
            <UpdatePassword buttonTheme={buttonTheme} />
          </Card>
        </Stack>
      }
    </>
  );
};

export default Profile;